import { useDispatch } from 'react-redux'
import {
  GETVehiclesResponse,
  IVehicleType,
  LinkVehicleToWorkorderResolvingAction,
  RecentlyOpenedEntityType,
  VehicleProvider
} from '@cbgms/api'
import { EmptyUUID, isNonEmptyUUID } from '@cbgms/base/utils/uuid'
import {
  clearVehicleLinkModalState,
  createVehicle,
  getVehiclesByVin,
  setLoadedVehicle,
  setVehicleLinkModalState,
  setVehicleLinkReceivedData,
  updateVehicle
} from 'app/vehicles/state/actions'
import { IGroup } from '@cbgms/base/utils/helpers'
import { useShowVehicleTypeSelector } from 'app/vehicles/components/SelectVehicleType/use-select-vehicle-type'
import { useShowDuplicateVinSelector } from 'app/vehicles/components/DuplicateVehicleFound/DuplicateVinContext'
import { useWorkorderBaseRoute } from 'utils/Subscription/pathAndRouteSelector'
import { useHistory } from 'react-router-dom'
import { useWorkorderContext } from 'contexts/WorkorderContext/use-workorder-context'
import { WoResolverMode } from 'contexts/WorkorderContext/use-determine-workorder-uuid-for-context'
import { useCallback } from 'react'
import { usePutNewRecentlyOpenedEntities } from './use-fetch-recently-opened-entities'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { linkVehicleToWorkorder } from 'app/workorder/state/workorder/actions'
import { generateApiError } from 'utils/Request/Error'
import { useNotifier } from '@cbgms/base/notifier'
import { useTranslation } from 'react-i18next'
import { getOrderWithRelated } from 'app/order/state/actions'

type PrepVehicleFunc = (vehicle: IGroup<IVehicleType>) => Promise<string | null>
const usePrepareVehicle = (): PrepVehicleFunc => {
  const dispatch = useDispatch()
  const navigation = useHistory()
  const putRecentlyOpenedEntity = usePutNewRecentlyOpenedEntities()

  const { showSelection: showVehicleTypeSelection } = useShowVehicleTypeSelector()
  const { showSelection: showVinSelection } = useShowDuplicateVinSelector()

  const handleMissingRegistrations = (vehicleItem: IVehicleType): boolean => {
    if (vehicleItem.Registration === '') {
      // remnant of EMDM integration. probably obsolete; the vehicle is without a registration,
      // allow user to fill in additional details. Exit the flow before creating an new order
      dispatch(setLoadedVehicle(vehicleItem))
      navigation.push('/vehicles/quick-create', vehicleItem)
      return false
    }
    return true
  }

  const handleVinDuplicates = async (vehicleItem: IVehicleType): Promise<boolean> => {
    // Check if there are vehicles with this VIN ( and length 17)
    if (vehicleItem.Vin && vehicleItem.Vin.length === 17) {
      try {
        const vehicleWithDuplicateVin = ((await dispatch(getVehiclesByVin(vehicleItem.Vin))) as any as GETVehiclesResponse).Data
        if (vehicleWithDuplicateVin.length) {
          await showVinSelection(vehicleItem, vehicleWithDuplicateVin[0].Vehicle)
          await dispatch(updateVehicle(vehicleWithDuplicateVin[0].Vehicle.UUID, { Vin: '' }))
        }
      } catch {
        return false
      }
    }
    return true
  }

  return useCallback(async (vehicle: IGroup<IVehicleType>) => {
    let vehicleUUID = vehicle.Values[0].UUID
    const isGenericVehicle = vehicleUUID === EmptyUUID

    if (isGenericVehicle) {
      // the vehicle doesn't exists yet, attempt to create a vehicle from the generic-vehicle
      const source = (vehicle.Values.find(v => v.GenericVehicle.Source)?.GenericVehicle.Source || '') as any as VehicleProvider
      const genericVehicleOptions = vehicle.Values.map(v => v.GenericVehicle)
      const genericVehicle = await showVehicleTypeSelection(genericVehicleOptions, source)
      const vehicleItem = vehicle.Values.find(v => v.GenericVehicle.ID === genericVehicle.ID)
      if (!vehicleItem) return null // break out when no selection was made

      let isSafe = handleMissingRegistrations(vehicleItem)
      if (!isSafe) {
        return null
      }
      isSafe = await handleVinDuplicates(vehicleItem)
      if (!isSafe) {
        return null
      }

      const { Data } = await dispatch(createVehicle(vehicleItem))
      vehicleUUID = Data.UUID
      if (vehicleUUID && isNonEmptyUUID(vehicleUUID)) {
        await putRecentlyOpenedEntity(RecentlyOpenedEntityType.Vehicle, vehicleUUID)
      }
    }
    return vehicleUUID
  }, [])
}

export const useOpenOrderForVehicle = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const notifier = useNotifier()
  const navigation = useHistory()
  const baseURL = useWorkorderBaseRoute()
  const { getWorkorderUUID } = useWorkorderContext()
  const prepareVehicle = usePrepareVehicle()
  const isPortal = useIsPortal()

  // Portal mode state management - Redux is used internally for modals

  // Vehicle link success callback
  const onVehicleLinkSuccess = useCallback(
    async (targetWorkorderUUID: string) => {
      try {
        notifier.success(t('workorder.vehicle_linked'))
        await dispatch(getOrderWithRelated(targetWorkorderUUID))
        navigation.push(`${baseURL}${targetWorkorderUUID}`)
      } catch (error) {
        console.error('Error in success callback:', error)
      }
    },
    [notifier, t, dispatch, navigation, baseURL]
  )

  // Link vehicle function (moved from portal hook)
  const linkVehicle = useCallback(
    async (vehicleUUID: string, vehicle: IGroup<IVehicleType>, actions: LinkVehicleToWorkorderResolvingAction[] = []) => {
      try {
        // Get workorderUUID with vehicle's account for proper association
        const workorderUUID = await getWorkorderUUID({
          accountUUID: vehicle.Values[0].AccountUUID
        })

        const { Data } = await dispatch(
          linkVehicleToWorkorder(workorderUUID, {
            VehicleUUID: vehicleUUID,
            Actions: actions
          })
        )

        dispatch(setVehicleLinkReceivedData(Data))

        if (Data.Rejections?.length) {
          dispatch(setVehicleLinkModalState('vehicle', true))
        } else if (Data.Warnings?.length) {
          dispatch(setVehicleLinkModalState('account', true))
        } else {
          dispatch(clearVehicleLinkModalState())
          await onVehicleLinkSuccess(workorderUUID)
        }
      } catch (e) {
        const apiError = generateApiError(e)
        notifier.error(apiError.error)
      }
    },
    [getWorkorderUUID, dispatch, onVehicleLinkSuccess, notifier]
  )

  const createOrderWithVehicleUUID = async (vehicleUUID: string, optionalAccountUUID?: string) => {
    const orderUUID = await getWorkorderUUID({
      accountUUID: optionalAccountUUID,
      vehicleUUID,
      mode: WoResolverMode.ForceNew
    })
    navigation.push(`${baseURL}${orderUUID}`)
  }

  const openOrder = async (vehicle: IGroup<IVehicleType>) => {
    const vehicleUUID = await prepareVehicle(vehicle)
    // at this point we should always have a uuid. though if the user aborted a manual selection then we should skip
    if (isNonEmptyUUID(vehicleUUID)) {
      if (isPortal && location.pathname.includes('/vehicles/link')) {
        // Portal behavior: Link vehicle to existing order (this handles modals automatically)
        await linkVehicle(vehicleUUID, vehicle)
      } else {
        // Non-portal behavior: Create new order for this vehicle and navigate to it
        createOrderWithVehicleUUID(vehicleUUID, vehicle.Values[0].AccountUUID)
      }
    }
  }

  // Always return the openOrder function for backward compatibility
  // Portal mode will use Redux for modal state management behind the scenes
  return openOrder
}
